import { useEffect, useRef } from 'react';

/**
 * Custom React hook for Mobile WebOTP API SMS Auto-Detection.
 * Restricted strictly to Mobile devices (Android/iOS).
 * Desktop functionality remains 100% unchanged.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Active when the OTP input step/modal is visible
 * @param {Function} options.onOtpReceived - Callback invoked with the parsed 6-digit code string
 */
export const useWebOTP = ({ enabled = false, onOtpReceived }) => {
  const onOtpReceivedRef = useRef(onOtpReceived);
  const lastProcessedOtpRef = useRef('');

  useEffect(() => {
    onOtpReceivedRef.current = onOtpReceived;
  }, [onOtpReceived]);

  // Reset last processed OTP when enabled changes or resets (e.g., on Resend OTP)
  useEffect(() => {
    if (!enabled) {
      lastProcessedOtpRef.current = '';
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof navigator === 'undefined') {
      return;
    }

    // Mobile Device Capability Check
    // Ensures WebOTP API listener ONLY runs on mobile environments (Android/iOS)
    const isMobileDevice = () => {
      const hasTouch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
      const isCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
      const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      return (hasTouch || isCoarsePointer) && isMobileUA;
    };

    if (!isMobileDevice()) {
      return;
    }

    const processCode = (rawCode, source) => {
      if (!rawCode) return;
      const str = String(rawCode);
      // Search for standalone 6-digit OTP (avoiding 10-digit phone numbers in SMS)
      const match = str.match(/\b\d{6}\b/);
      const cleanDigits = match ? match[0] : str.replace(/\D/g, '').slice(0, 6);

      if (cleanDigits.length === 6 && cleanDigits !== lastProcessedOtpRef.current) {
        lastProcessedOtpRef.current = cleanDigits;
        console.log(`[OTP AutoFill] 6-Digit Code detected via ${source}: ${cleanDigits}`);
        if (typeof onOtpReceivedRef.current === 'function') {
          onOtpReceivedRef.current(cleanDigits);
        }
      }
    };

    // Browser Native WebOTP API (For Android Chrome / supported mobile browsers)
    let abortController = null;
    if ('OTPCredential' in window && navigator.credentials) {
      abortController = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: abortController.signal,
        })
        .then((otpCredential) => {
          console.log('[WebOTP API Resolved]', otpCredential);
          const rawCode = otpCredential?.code || otpCredential?.value || (typeof otpCredential === 'string' ? otpCredential : '');
          if (rawCode) {
            processCode(rawCode, 'WebOTP API');
          }
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            console.warn('[WebOTP] SMS detection notice:', err.message || err);
          }
        });
    }

    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [enabled]);
};

export default useWebOTP;

